import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Building2, Briefcase, Wallet, Award, Clock, UserCheck, FileText } from "lucide-react";
import { api, ghsOnly as cedi, fmtDate, errMsg, downloadResume } from "../../lib/api";
import { PageHeader, StatCard, StatusPill, BeltBadge } from "../../components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { TrainingsManager, TrainingRequests } from "./AdminTrainings";

const UsersTable = ({ users, onToggle }) => (
  <div className="dx-card overflow-x-auto">
    <table className="w-full text-sm" data-testid="admin-users-table">
      <thead className="bg-[#FAF8FC] text-left text-xs uppercase tracking-wider text-[#8a7a92]">
        <tr>{["Name", "Email", "Role", "Location", "Joined", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y">
        {users.map((u) => (
          <tr key={u.id} data-testid={`admin-user-${u.id}`}>
            <td className="px-4 py-3 font-semibold">{u.name}{u.company_name && u.role === "employer" ? <div className="text-xs text-[#6b5a73] font-normal">{u.company_name}</div> : null}</td>
            <td className="px-4 py-3">{u.email}</td>
            <td className="px-4 py-3 capitalize">{u.role}</td>
            <td className="px-4 py-3">{u.location || "-"}</td>
            <td className="px-4 py-3 whitespace-nowrap">{fmtDate(u.created_at)}</td>
            <td className="px-4 py-3 text-right">
              {u.role !== "admin" && <button className={u.active === false ? "dx-btn-gold py-1.5" : "dx-btn-ghost py-1.5"} onClick={() => onToggle(u.id)} data-testid={`admin-user-${u.id}-toggle`}>{u.active === false ? "Reactivate" : "Suspend"}</button>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PaymentsTable = ({ payments }) => (
  <div className="dx-card overflow-x-auto">
    <table className="w-full text-sm" data-testid="admin-payments-table">
      <thead className="bg-[#FAF8FC] text-left text-xs uppercase tracking-wider text-[#8a7a92]">
        <tr>{["Reference", "Staff", "Course", "Provider", "Amount", "Date", "Status"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y">
        {payments.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6b5a73]">No payments yet.</td></tr>}
        {payments.map((p) => (
          <tr key={p.id}>
            <td className="px-4 py-3 font-mono text-xs">{p.reference}</td>
            <td className="px-4 py-3">{p.user_name}</td>
            <td className="px-4 py-3">{p.course_title}</td>
            <td className="px-4 py-3">{p.provider} · {p.phone}</td>
            <td className="px-4 py-3 font-semibold">{cedi(p.amount)}</td>
            <td className="px-4 py-3 whitespace-nowrap">{fmtDate(p.created_at)}</td>
            <td className="px-4 py-3"><StatusPill status={p.status === "success" ? "done" : "pending"} />{p.mocked && <div className="text-[10px] text-[#8a6a00] font-bold mt-1">SIMULATED</div>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ResumeQueue = ({ rows, onUpload }) => (
  <div className="dx-card overflow-x-auto">
    <table className="w-full text-sm" data-testid="admin-resumes-table">
      <thead className="bg-[#FAF8FC] text-left text-xs uppercase tracking-wider text-[#8a7a92]">
        <tr>{["Member", "Belt", "Resume", "Status", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
      </thead>
      <tbody className="divide-y">
        {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6b5a73]">No members yet.</td></tr>}
        {rows.map((p) => (
          <tr key={p.user_id} data-testid={`admin-resume-${p.user_id}`}>
            <td className="px-4 py-3"><div className="font-semibold">{p.name}</div><div className="text-xs text-[#6b5a73]">{p.email} · {p.phone || "no phone"}</div></td>
            <td className="px-4 py-3"><BeltBadge belt={p.highest_belt} /></td>
            <td className="px-4 py-3">{p.resume_filename ? <button className="text-[#5B2A6E] underline break-all text-left" onClick={() => downloadResume(p.user_id, p.resume_filename)} data-testid={`admin-resume-${p.user_id}-download`}>{p.resume_filename}</button> : <span className="text-[#8a7a92]">Not uploaded</span>}</td>
            <td className="px-4 py-3"><StatusPill status={p.resume_status === "reviewed" ? "done" : p.resume_status === "submitted" ? "in_progress" : "pending"} /></td>
            <td className="px-4 py-3 text-right">
              <label className="dx-btn-ghost py-1.5 cursor-pointer" data-testid={`admin-resume-${p.user_id}-upload`}>
                Upload tidied version
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(p.user_id, e.target.files[0])} />
              </label>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AdminHome() {
  const [s, setS] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [role, setRole] = useState("");

  const load = () => Promise.all([api.get("/admin/stats"), api.get("/admin/users", { params: { role } }), api.get("/admin/payments"), api.get("/admin/resumes")])
    .then(([a, b, c, d]) => { setS(a.data); setUsers(b.data); setPayments(c.data); setResumes(d.data); });
  useEffect(() => { load(); }, [role]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (id) => { try { await api.patch(`/admin/users/${id}/active`); toast.success("Account updated"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const uploadTidied = async (uid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/admin/resumes/${uid}`, fd); toast.success("Tidied resume saved to member profile"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!s) return null;

  return (
    <div>
      <PageHeader eyebrow="Dexterity Consult" title="Admin overview" subtitle="Memberships, resume reviews, training revenue and workforce activity across the platform." />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard accent label="Total revenue" value={cedi(s.revenue)} icon={Wallet} testid="admin-stat-revenue" />
        <StatCard label="Staff" value={s.staff} icon={Users} testid="admin-stat-staff" />
        <StatCard label="Employers" value={s.employers} icon={Building2} testid="admin-stat-employers" />
        <StatCard label="Open jobs" value={s.open_jobs} icon={Briefcase} testid="admin-stat-jobs" />
        <StatCard label="Applications" value={s.applications} icon={FileText} testid="admin-stat-applications" />
        <StatCard label="Hires" value={s.hires} icon={UserCheck} testid="admin-stat-hires" />
        <StatCard label="Certificates" value={s.certificates} icon={Award} testid="admin-stat-certificates" />
        <StatCard label="Clock-ins today" value={s.clockins_today} icon={Clock} testid="admin-stat-clockins" />
      </div>
      <div className="dx-card p-6 mt-5 grid grid-cols-3 gap-4 text-center" data-testid="admin-belts">
        {[["Yellow", "yellow", "#F3C623"], ["Green", "green", "#22c55e"], ["Black", "black", "#111827"]].map(([l, k, c]) => (
          <div key={k}><div className="font-display text-3xl font-bold" style={{ color: c === "#F3C623" ? "#B88A00" : c }}>{s.belts[k]}</div><div className="text-xs text-[#6b5a73] mt-1">{l} Belts certified</div></div>
        ))}
      </div>
      <Tabs defaultValue="users" className="mt-8">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="trainings" data-testid="admin-tab-trainings">Upcoming trainings</TabsTrigger>
          <TabsTrigger value="requests" data-testid="admin-tab-requests">Company requests</TabsTrigger>
          <TabsTrigger value="resumes" data-testid="admin-tab-resumes">Resume reviews ({resumes.filter((r) => r.resume_status === "submitted").length})</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="users">
          <select className="dx-input w-auto mb-3" value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-role-filter">
            <option value="">All roles</option><option value="staff">Staff</option><option value="employer">Employers</option><option value="admin">Admins</option>
          </select>
          <UsersTable users={users} onToggle={toggle} />
        </TabsContent>
        <TabsContent value="resumes"><ResumeQueue rows={resumes} onUpload={uploadTidied} /></TabsContent>
        <TabsContent value="trainings"><TrainingsManager /></TabsContent>
        <TabsContent value="requests"><TrainingRequests /></TabsContent>
        <TabsContent value="payments"><PaymentsTable payments={payments} /></TabsContent>
      </Tabs>
    </div>
  );
}
